import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listUsers } from '../../../api/admin'
import type { V2User } from '../../../api/schemas'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { StatusBadge } from '../../../components/common/Status'
import { TimeAgo } from '../../../components/common/TimeAgo'
import { Skeleton } from '../../../components/ui/skeleton'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { formatNum } from '../../../lib/format'

const PAGE_SIZE = 20

export function UsersSection() {
  const [regionCode, setRegionCode] = useState('')
  const [applied, setApplied] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['v2', 'users', { regionCode: applied, page }],
    queryFn: () => listUsers({ regionCode: applied || undefined, page, pageSize: PAGE_SIZE }),
    retry: 1,
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="panel-glass overflow-hidden rounded-xl">
      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
        <div>
          <Label htmlFor="region-code">区域代码（非超管必填，留空查全局）</Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              id="region-code"
              value={regionCode}
              onChange={(e) => setRegionCode(e.target.value)}
              placeholder="如 650000"
              className="w-48"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setApplied(regionCode.trim())
                setPage(1)
              }}
            >
              查询
            </Button>
          </div>
        </div>
      </div>

      <QueryBoundary
        query={query}
        skeleton={<div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>}
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '无用户记录' }}
      >
        {(d) => (
          <>
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">用户 ID</th>
                  <th className="px-4 py-2.5 font-medium">姓名</th>
                  <th className="px-4 py-2.5 font-medium">邮箱</th>
                  <th className="px-4 py-2.5 font-medium">区域</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                  <th className="px-4 py-2.5 font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.items.map((u: V2User) => (
                  <tr key={u.userId} className="text-xs transition hover:bg-foreground/[.03]">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-foreground/90">{u.userId}</td>
                    <td className="px-4 py-2.5 text-foreground/90">{u.displayName || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{u.email ?? '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{u.regionCode ?? '全局'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={u.status || 'active'} /></td>
                    <td className="px-4 py-2.5"><TimeAgo ts={u.createdAt} className="text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>共 {formatNum(d.total)} 条 · 第 {page}/{totalPages} 页</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一页</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</Button>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  )
}
